import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import { formatNumberSync } from "@/utils/numberFormatter";
import { clearNotaComercialPendingCache } from "./notaComercialPendingContacts";

/** Lados soportados: a quién pertenecen las notas que se ofrecen. */
export const SIDE_CLIENT = "client";
export const SIDE_SUPPLIER = "supplier";

/** Operación a la que se aplica la nota en cada lado. */
export const TARGET_BY_SIDE = {
  [SIDE_CLIENT]: "proform",
  [SIDE_SUPPLIER]: "shipping",
};

function refId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const id = value._id || value.id;
    return id != null ? String(id) : "";
  }
  return String(value);
}

function money(value) {
  try {
    return formatNumberSync(value, null, true);
  } catch (e) {
    return String(value ?? "");
  }
}

function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Etiqueta corta del tipo de nota; en proveedor conviven crédito y débito. */
function kindLabel(note, translate) {
  const cd = String(note.credit_debit || "").toLowerCase();
  if (cd.startsWith("debit")) return translate("notaComercial.offerOnCreate.kindDebit");
  if (cd.startsWith("credit")) return translate("notaComercial.offerOnCreate.kindCredit");
  return "";
}

function noteRowHtml(note, idx, translate) {
  const checked = idx === 0 ? "checked" : "";
  const title = escapeHtml(note.number || note.name || note._id);
  const kind = kindLabel(note, translate);
  const kindHtml = kind
    ? `<span style="font-size:11px;border:1px solid #adb5bd;border-radius:4px;padding:1px 5px;margin-left:6px;">${escapeHtml(kind)}</span>`
    : "";
  const max = Number(note.available) || 0;
  return `
    <label style="display:block;text-align:left;border:1px solid #dee2e6;border-radius:6px;padding:10px;margin:8px 0;cursor:pointer;">
      <input type="radio" name="ncOfferNote" value="${escapeHtml(note._id)}" ${checked} style="margin-right:8px;" />
      <strong>${title}</strong>${kindHtml}<br/>
      <span style="font-size:12px;color:#6c757d;">
        ${escapeHtml(translate("notaComercial.offerOnCreate.totalAmount"))}: ${escapeHtml(money(note.amount))}
        · ${escapeHtml(translate("notaComercial.offerOnCreate.availableBalance"))}: ${escapeHtml(money(note.available))}
      </span>
      <div style="margin-top:6px;margin-left:22px;">
        <label style="font-size:12px;">${escapeHtml(translate("notaComercial.offerOnCreate.applyAmount"))}</label>
        <input type="number" class="swal2-input nc-offer-amt" data-note="${escapeHtml(note._id)}"
          value="${max}" min="0" max="${max}" step="any"
          style="width:100%;margin:4px 0 0;height:2.2em;" />
      </div>
    </label>`;
}

/**
 * Notas del contacto con saldo, del lado pedido. No filtra moneda.
 * @param {string} contactId
 * @param {'client'|'supplier'} side
 * @returns {Promise<{moduleActive: boolean, offerOnCreateEnabled: boolean, notes: object[]}|null>}
 *   null si la consulta falla.
 */
export async function fetchAvailableNotes(contactId, side) {
  const cid = refId(contactId);
  if (!cid) return { moduleActive: false, offerOnCreateEnabled: false, notes: [] };
  const normalizedSide = side === SIDE_SUPPLIER ? SIDE_SUPPLIER : SIDE_CLIENT;
  try {
    const { data } = await wencesApi.get("/api/nota-comercial/offer-on-create/available", {
      params: { contactId: cid, side: normalizedSide },
    });
    return {
      moduleActive: !!data?.moduleActive,
      offerOnCreateEnabled: !!data?.offerOnCreateEnabled,
      notes: Array.isArray(data?.notes) ? data.notes : [],
    };
  } catch (err) {
    return null;
  }
}

/**
 * Popup para elegir una de las notas y cuánto aplicar.
 * @param {object[]} notes
 * @param {'client'|'supplier'} side
 * @param {(key: string) => string} t
 * @returns {Promise<null|{noteId: string, amount: number}>}
 */
export async function pickNotaComercialToApply(notes, side, t) {
  const translate = typeof t === "function" ? t : (k) => k;
  const normalizedSide = side === SIDE_SUPPLIER ? SIDE_SUPPLIER : SIDE_CLIENT;
  if (!Array.isArray(notes) || !notes.length) return null;

  const subtitleKey =
    normalizedSide === SIDE_SUPPLIER
      ? "notaComercial.offerOnCreate.subtitleSupplier"
      : "notaComercial.offerOnCreate.subtitleClient";

  const result = await Swal.fire({
    title: translate("notaComercial.offerOnCreate.title"),
    html: `
      <p style="text-align:left;color:#6c757d;margin-bottom:8px;">
        ${escapeHtml(translate(subtitleKey))}
      </p>
      ${notes.map((n, i) => noteRowHtml(n, i, translate)).join("")}
    `,
    showCancelButton: true,
    confirmButtonText: translate("notaComercial.offerOnCreate.confirm"),
    cancelButtonText: translate("notaComercial.offerOnCreate.skip"),
    focusConfirm: false,
    width: 640,
    heightAuto: false,
    allowOutsideClick: false,
    customClass: { container: "nc-offer-swal-container" },
    didOpen: () => {
      const el = document.querySelector(".nc-offer-swal-container");
      if (el) el.style.zIndex = "20000";
    },
    preConfirm: () => {
      const selected = document.querySelector('input[name="ncOfferNote"]:checked');
      if (!selected || !selected.value) return null;
      const noteId = selected.value;
      const note = notes.find((n) => n._id === noteId);
      const input = document.querySelector(`.nc-offer-amt[data-note="${noteId}"]`);
      let amount = Number(input && input.value);
      if (!note || !Number.isFinite(amount) || amount <= 0) {
        Swal.showValidationMessage(translate("notaComercial.noteForm.validationAmount"));
        return false;
      }
      const max = Number(note.available) || 0;
      if (amount > max) amount = max;
      return { noteId, amount };
    },
  });

  if (!result.isConfirmed) return null;
  return result.value || null;
}

/**
 * Al elegir el contacto de una operación: lista sus notas con saldo y pide elegir una.
 * Solo corre si el popup automático está habilitado para el tenant.
 * @returns {Promise<null|'feature_off'|'no_notes'|'error'|{noteId: string, amount: number}>}
 */
export async function askOfferNotaComercialForContact(contactId, side, t) {
  const data = await fetchAvailableNotes(contactId, side);
  if (!data) return "error";
  if (!data.offerOnCreateEnabled) return "feature_off";
  if (!data.notes.length) return "no_notes";
  return pickNotaComercialToApply(data.notes, side, t);
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Aplica la elección pendiente a la operación ya creada.
 * Reintenta ante los códigos que delatan la carrera con el autosave.
 * @param {string} targetId
 * @param {{noteId: string, amount: number, side?: string, contactId?: string}} choice
 * @param {(key: string) => string} t
 * @param {{contactId?: string, maxAttempts?: number, showError?: boolean}} options
 * @returns {Promise<boolean>}
 */
export async function applyOfferedNotaComercial(targetId, choice, t, options = {}) {
  const translate = typeof t === "function" ? t : (k) => k;
  const id = targetId != null ? String(targetId).trim() : "";
  const amount = Number(choice?.amount);
  const noteId = choice?.noteId != null ? String(choice.noteId) : "";
  const side = choice?.side === SIDE_SUPPLIER ? SIDE_SUPPLIER : SIDE_CLIENT;
  // El destino no siempre es el que implica el lado: una nota del cliente se puede
  // aplicar a un embarque, no solo a la proforma.
  const targetType = options.targetType || choice?.targetType || TARGET_BY_SIDE[side];
  const contactId =
    choice?.contactId != null ? String(choice.contactId) : options.contactId || "";
  const maxAttempts = Number(options.maxAttempts) > 0 ? Number(options.maxAttempts) : 5;
  // Gasto elegido: el backend le pone ese rol a la nota, para que quede bajo ese gasto.
  const expenseId = options.expenseId ? String(options.expenseId) : "";
  const showError = options.showError !== false;

  if (!id || !noteId || !(amount > 0)) return false;

  let lastCode = "";
  let lastDetail = "";
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await wencesApi.post("/api/nota-comercial/offer-on-create/apply", {
        noteId,
        targetId: id,
        targetType,
        side,
        amount,
        contactId: contactId || undefined,
        expenseId: expenseId || undefined,
      });
      // El aviso de notas pendientes de ese contacto quedó viejo.
      clearNotaComercialPendingCache(contactId);
      return true;
    } catch (err) {
      lastCode = err?.response?.data?.error || err?.message || "APPLY_FAILED";
      lastDetail = err?.response?.data?.detail || "";
      const retryable = lastCode === "CONTACT_MISMATCH" || lastCode === "TARGET_NOT_FOUND";
      if (retryable && attempt < maxAttempts) {
        await sleep(400 * attempt);
        continue;
      }
      break;
    }
  }

  if (showError) {
    let text;
    if (lastCode === "AMOUNT_EXCEEDS_AVAILABLE") {
      text = translate("notaComercial.offerOnCreate.applyErrorExceeds");
    } else if (lastCode === "LINE_NOT_VALID" && lastDetail) {
      text = lastDetail;
    } else {
      text = translate("notaComercial.offerOnCreate.applyErrorGeneric");
    }
    await Swal.fire({
      icon: "warning",
      title: translate("notaComercial.offerOnCreate.applyErrorTitle"),
      text,
    });
  }
  return false;
}

/**
 * Deshace la aplicación de una nota a una operación.
 * @param {string} noteId
 * @param {{targetType: 'proform'|'shipping', targetId: string}} target
 * @returns {Promise<{ok: true, removedAmount: number}|{ok: false, code: string}>}
 */
export async function unapplyNotaComercial(noteId, target = {}) {
  const id = noteId != null ? String(noteId).trim() : "";
  const targetId = target.targetId != null ? String(target.targetId).trim() : "";
  const targetType = target.targetType === "shipping" ? "shipping" : "proform";
  if (!id || !targetId) return { ok: false, code: "INVALID_IDS" };
  try {
    const { data } = await wencesApi.post("/api/nota-comercial/offer-on-create/unapply", {
      noteId: id,
      targetId,
      targetType,
    });
    return { ok: true, removedAmount: Number(data?.removedAmount) || 0 };
  } catch (err) {
    return { ok: false, code: err?.response?.data?.error || "UNAPPLY_FAILED" };
  }
}
